// PendingInvites.js 

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Mail, Check, X, Calendar } from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  getDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { createNotification } from '../../services/notificationService';

function PendingInvites({ onClose }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;
        
        console.log("Current user email:", user?.email); // Debug log
        
        if (!user || !user.email) {
          console.log("No user or email found"); // Debug log
          setLoading(false);
          return;
        }

        // Query for pending invitations by email
        const invitesQuery = query(
          collection(db, 'invitations'),
          where('email', '==', user.email),
          where('status', '==', 'pending')
        );

        console.log("Running query for email:", user.email); // Debug log
        
        const invitesSnapshot = await getDocs(invitesQuery);
        console.log("Found invitations:", invitesSnapshot.docs.length); // Debug log
        invitesSnapshot.docs.forEach(doc => {
          console.log("Invitation data:", doc.id, doc.data()); // Log each invitation
        });
        
        const invitesData = invitesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setInvitations(invitesData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching invitations:', err);
        setError('Error loading invitations');
        setLoading(false);
      }
    };

    fetchInvitations();
  }, []);

  const handleInviteAction = async (invitationId, action) => {
    try {
      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) return;

      const inviteRef = doc(db, 'invitations', invitationId);
      const groupRef = doc(db, 'groups', invitation.groupId);
      
      // Update invitation status
      await updateDoc(inviteRef, {
        status: action,
        updatedAt: serverTimestamp()
      });

      // Get current user
      const user = auth.currentUser;

      if (action === 'accepted') {
        // Add user to group members
        const groupSnap = await getDoc(groupRef);
        if (!groupSnap.exists()) {
          throw new Error('Group not found');
        }
        
        const groupData = groupSnap.data();
        
        // Add user to group members
        const updatedMembers = { 
          ...groupData.members,
          [user.uid]: {
            displayName: user.displayName || 'User',
            email: user.email,
            status: true,
            lastUpdated: new Date()
          }
        };
        
        // Update group with new member
        await updateDoc(groupRef, {
          members: updatedMembers
        });
        
        // Create notification for the user who accepted
        await createNotification(
          user.uid,
          'membership',
          'joined',
          `You joined ${invitation.groupName}`,
          {
            relatedGroupId: invitation.groupId,
            relatedGroupName: invitation.groupName
          }
        );
        
        // Create notification for the group creator
        await createNotification(
          invitation.invitedBy,
          'membership',
          'joined',
          `${user.displayName || 'Someone'} joined ${invitation.groupName}`,
          {
            relatedGroupId: invitation.groupId,
            relatedGroupName: invitation.groupName,
            relatedUserId: user.uid,
            relatedUserName: user.displayName || 'Someone'
          }
        );
      } else {
        // Create notification for user who declined
        await createNotification(
          user.uid,
          'invite',
          'declined',
          `You declined invitation to ${invitation.groupName}`,
          {
            relatedGroupId: invitation.groupId,
            relatedGroupName: invitation.groupName
          }
        );
        
        // Only notify the inviter if they exist
        if (invitation.invitedBy) {
          await createNotification(
            invitation.invitedBy,
            'invite',
            'declined',
            `${user.displayName || 'Someone'} declined your invitation to ${invitation.groupName}`,
            {
              relatedGroupId: invitation.groupId,
              relatedGroupName: invitation.groupName,
              relatedUserId: user.uid,
              relatedUserName: user.displayName || 'Someone'
            }
          );
        }
      }

      // Remove the handled invitation from state
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
      
    } catch (err) {
      console.error(`Error ${action} invitation:`, err);
      setError(`Failed to ${action} invitation`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    // Format as "Mar 5, 2025"
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="text-center py-6">Loading invitations...</div>;
  }

  if (error) {
    return <div className="text-red-600 py-6">{error}</div>;
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Mail className="h-6 w-6 text-gray-400" />
        </div>
        <h4 className="text-gray-700 font-medium mb-1">No pending invitations</h4>
        <p className="text-gray-500 text-sm">When someone invites you to a group, it will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Pending Invitations</h3>
      
      {invitations.map(invitation => (
        <div key={invitation.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-3">
          <div className="p-4">
            <div className="mb-3">
              <div className="flex justify-between items-start">
                <h4 className="text-lg font-semibold text-gray-900">{invitation.groupName}</h4>
                <div className="flex items-center text-xs text-gray-500">
                  <Calendar className="h-3 w-3 mr-1" />
                  Sent {formatDate(invitation.createdAt)}
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Invited by <span className="font-medium">{invitation.invitedByName || 'Someone'}</span>
              </p>
            </div>
            
            {invitation.message && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-700 italic">
                  "{invitation.message}"
                </p>
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="text-red-600  hover:bg-red-20 flex items-center" // border-red-200
                onClick={() => handleInviteAction(invitation.id, 'declined')}
              >
                <X className="h-4 w-4 mr-1" />
                Decline
              </Button>
              <Button
                size="sm"
                className="text-emerald-600 hover:bg-emerald-20 flex items-center" 
                // Changed from green-600/700 to emerald-600/700 for a nicer green
                onClick={() => handleInviteAction(invitation.id, 'accepted')}
              >
                <Check className="h-4 w-4 mr-1" />
                Accept
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default PendingInvites;